<script lang="ts">
  import type { ssoProviders } from "$lib/utils/constants";
  import { CircleCheck, Clock, Dot, Hourglass, Plus } from "lucide-svelte";
  import { push } from "svelte-spa-router";
  import { selectedOrgStore } from "$lib/stores/orgs";
  import { Button } from "../button";

  type Provider = typeof ssoProviders.entra;

  interface Props {
    invitedAdmins: string[];
    provider: Provider;
    resendAdminInvite: (admin: string, providerName: string) => void;
    removeAdminInvite: (admin: string, providerName: string) => void;
    onAddAdmin: (providerName: string) => void;
    connected_by?: string;
  }

  const { invitedAdmins, provider, removeAdminInvite, resendAdminInvite, connected_by, onAddAdmin }: Props = $props();

  function handleClick(provider: Provider) {
    // TODO: onboarding pages need to include provider in query params to support this working dynamically in the future
    // TODO: hard-coded provider ids
    if (provider.name === "Microsoft Entra") {
      push(`${$selectedOrgStore?.pagePrefix}/connect_sso?action=connect&provider=entra`);
    } else if (provider.name === "Sharepoint") {
      push(`${$selectedOrgStore?.pagePrefix}/connect_data?action=connect&provider=sharepoint`);
    } else {
      push(`${$selectedOrgStore?.pagePrefix}/connect_data`);
    }
  }
</script>

<div class="flex flex-col gap-2 w-4/5 bg-white p-4 rounded-md shadow-sm border border-gray-200">
  <div class="flex gap-4 items-center">
    <div class="w-12 h-12 rounded-md border border-offwhite-100 flex items-center justify-center">
      <img src={provider.logo} alt={provider.name} class="w-8 h-8" />
    </div>
    <!--TODO: render connected status based on "connected_by"-->
    <div class="flex flex-col gap-1 flex-grow">
      <span class="text-md">{provider.name}</span>
      {#if connected_by}
        <div><span class="font-thin">Connected by </span><span class="font-medium ml-1">{connected_by}</span></div>
      {:else}
        <span class="font-thin">Awaiting permission</span>
      {/if}
    </div>
    <div>
      {#if connected_by}
        <div
          class="min-w-36 flex justify-center gap-1 items-center text-green-600 border border-green-400 rounded-md px-3 py-2"
        >
          <CircleCheck class="w-6 h-6" /><span class="font-medium">Connected</span>
        </div>
      {:else}
        <div
          class="min-w-36 flex justify-center gap-1 items-center text-offblack-600 border border-offblack-400 rounded-md px-3 py-2"
        >
          <Hourglass class="w-6 h-6" /><span class="font-medium">Requested</span>
        </div>
      {/if}
    </div>
  </div>
  {#if invitedAdmins.length > 0 || !connected_by}
    <div class="relative inset-0 flex items-center my-3" aria-hidden="true">
      <div class="w-full border-t border-gray-300"></div>
    </div>
  {/if}

  {#if invitedAdmins.length > 0}
    <div class="flex flex-col gap-3">
      {#each invitedAdmins as admin}
        <div class="rounded-md border border-gray-300 p-4">
          <span class="font-thin">Access requested from <span class="font-medium">{admin}</span></span>
          <div class="flex gap-3 mt-2">
            <button class="font-semibold" onclick={() => resendAdminInvite(admin, provider.name)}>
              Resend Request
            </button>
            <Dot></Dot>
            <button class="font-semibold text-offblack-800" onclick={() => removeAdminInvite(admin, provider.name)}>
              Remove
            </button>
          </div>
        </div>
      {/each}
    </div>
  {/if}

  {#if !connected_by}
    <div class="flex">
      <button
        tabindex="0"
        onclick={() => onAddAdmin(provider.id)}
        class="flex gap-1 cursor-pointer hover:bg-offwhite-50 px-1 py-1 rounded-md duration-200 transition"
      >
        <Plus></Plus> Add another admin
      </button>
    </div>
  {/if}
</div>
